import { Box, TouchableBox, AHText, AHIcon, StoreFormattedDate, useScaleFactor, useTheme, TodoPrioIcon } from "arkh";
import React, { useMemo } from "react";

import { graphql, useFragment } from "react-relay";

import { SignalOverviewListItemMedium_Signal$key } from "./__generated__/SignalOverviewListItemMedium_Signal.graphql";
import { useEvent } from "../useEvent";
import { UrgentAnimation } from "../TodoListItem/UrgentAnimation";

type SignalListItemProps = {
  onPress?: (signalId: string, focused: boolean) => void;
  signal: SignalOverviewListItemMedium_Signal$key;
  isFocused?: boolean;
};

function SignalOverviewListItemMediumC(props: SignalListItemProps) {
  const signal = useFragment(
    graphql`
      fragment SignalOverviewListItemMedium_Signal on Signal {
        id
        title
        priority
        publishedAtDate {
          ...StoreFormattedDate
        }
        employeeReceiverReceiptsCount
        store {
          employeeCount
        }
        linkedProducts {
          id
        }
        descriptionImages {
          id
        }
        descriptionFiles {
          id
        }
      }
    `,
    props.signal
  );

  const itemId = signal.id;

  const op = props.onPress;

  const onPress = useEvent(() => {
    op?.(itemId, props.isFocused ?? false);
  });

  const isFocused = props.isFocused;

  const sf = useScaleFactor();
  const bl = 4;
  const borderLeftWidth = Math.floor(sf * bl);

  const theme = useTheme();

  const hasLinkedContent = useMemo(() => {
    return (
      signal.linkedProducts.length > 0 ||
      signal.descriptionImages.length > 0 ||
      signal.descriptionFiles.length > 0
    );
  }, [signal.linkedProducts, signal.descriptionImages, signal.descriptionFiles]);

  return (
    <TouchableBox
      spaceVertical="small"
      onPress={onPress}
      insets={{
        left: -bl,
      }}
      style={{
        overflow: "hidden",
        flexDirection: "row",
        alignItems: "center",
        borderLeftWidth: borderLeftWidth,
        borderColor: isFocused ? theme.primary : "transparent",
      }}
      spaceLeft="small"
    >
      <Box
        spaceRight="small"
        style={{
          flex: 1,
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box
          style={{
            flex: 1,
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          {signal.priority !== "DEFAULT" ? (
            <Box spaceRight="small">
              {signal.priority === "URGENT" ? <UrgentAnimation /> : null}
              <TodoPrioIcon priority={signal.priority} hideDefault />
            </Box>
          ) : null}

          <Box
            style={{
              flex: 1,
              flexDirection: "column",
            }}
          >
            <AHText
              type="subtitle"
              color="text-primary"
              numberOfLines={1}
            >
              {signal.title}
            </AHText>

            {hasLinkedContent ? (
              <Box
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 8,
                  marginTop: 4,
                }}
              >
                {signal.linkedProducts.length > 0 ? (
                  <AHIcon type="product" color="text-washed" size="small" />
                ) : null}
                {signal.descriptionFiles.length > 0 ? (
                  <AHIcon type="files" color="text-washed" size="small" />
                ) : null}
                {signal.descriptionImages.length > 0 ? (
                  <AHIcon type="image" color="text-washed" size="small" />
                ) : null}
              </Box>
            ) : null}
          </Box>
        </Box>

        <Box
          style={{
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 4,
            marginLeft: 12,
          }}
        >
          {signal.publishedAtDate ? (
            <AHText type="body" color="text-secondary">
              <StoreFormattedDate date={signal.publishedAtDate} />
            </AHText>
          ) : null}
          <Box style={{ flexDirection: "row", alignItems: "center", gap: 4 }}>
            <AHIcon type="eye" size="small" color="text-secondary" />
            <AHText type="body" color="text-secondary">
              {signal.employeeReceiverReceiptsCount}/{signal.store.employeeCount}
            </AHText>
          </Box>
        </Box>
      </Box>
    </TouchableBox>
  );
}

export const SignalOverviewListItemMedium = React.memo(
  SignalOverviewListItemMediumC
);
